"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChatMlArraySchema = exports.ChatMlMessageSchema = exports.SimpleChatMlArraySchema = exports.BaseChatMlMessageSchema = exports.isOpenAIImageContentPart = exports.isOpenAITextContentPart = exports.OpenAIContentSchema = exports.isMediaReferencePart = exports.OpenAIContentParts = exports.MediaReferencePartSchema = exports.OpenAIUrlImageUrl = exports.RedactedThinkingContentPartSchema = exports.ThinkingContentPartSchema = exports.MediaReferenceStringSchema = exports.MEDIA_REFERENCE_PATTERN = exports.ParsedMediaReferenceSchema = exports.ToolCallSchema = exports.ToolDefinitionSchema = void 0;
const zod_1 = require("zod");
/**
 * Schema for tool/function definitions in ChatML.
 * Used to define available tools for LLM function calling.
 */
exports.ToolDefinitionSchema = zod_1.z.object({
    name: zod_1.z.string(),
    description: zod_1.z.string().optional(),
    parameters: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(),
});
/**
 * Schema for tool/function call invocations in ChatML.
 * Represents an LLM's request to call a specific tool.
 */
exports.ToolCallSchema = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
    arguments: zod_1.z.string(), // JSON string of arguments
    type: zod_1.z.string().optional(),
    index: zod_1.z.number().optional(),
});
/**
 * Parsed Langfuse media reference.
 * Extracted from magic string format: @@@langfuseMedia:type=X|id=Y|source=Z@@@
 */
exports.ParsedMediaReferenceSchema = zod_1.z.object({
    type: zod_1.z.string(),
    id: zod_1.z.string(),
    source: zod_1.z.string(),
    referenceString: zod_1.z.string(),
});
// TODO: move to parser
// Matches the format parsed by MediaReferenceStringSchema. Non-greedy, so
// consecutive references in one string are separate matches.
exports.MEDIA_REFERENCE_PATTERN = /@@@langfuseMedia:.+?@@@/g;
/**
 * Schema that parses Langfuse media reference magic strings.
 * Format: @@@langfuseMedia:type=image/jpeg|id=<uuid>|source=base64@@@
 *
 * Note: This schema uses transforms that can throw errors during validation
 * if the magic string format is invalid. Always use with try-catch or safeParse.
 */
// TODO: move to parser
exports.MediaReferenceStringSchema = zod_1.z
    .string()
    .transform((str, ctx) => {
    // @@@langfuseMedia:type=image/jpeg|id=cc48838a-3da8-4ca4-a007-2cf8df930e69|source=base64@@@
    const magicStringPattern = /^@@@langfuseMedia:(.*)@@@$/;
    const match = str.match(magicStringPattern);
    if (!match) {
        ctx.addIssue({
            code: "custom",
            message: "Invalid langfuseMedia magic string format",
        });
        return zod_1.z.NEVER;
    }
    const content = match[1];
    const parts = content.split("|").filter(Boolean);
    const metadata = {
        referenceString: str,
    };
    for (const part of parts) {
        const [key, value] = part.split("=");
        if (key && value !== undefined) {
            metadata[key.trim()] = value.trim();
        }
        else {
            ctx.addIssue({
                code: "custom",
                message: `Invalid key-value pair: ${part}`,
            });
            return zod_1.z.NEVER;
        }
    }
    return metadata;
})
    .pipe(exports.ParsedMediaReferenceSchema);
/**
 * OpenAI input audio content part.
 * Used for audio inputs in multimodal chat completions.
 */
const OpenAIInputAudioContentPart = zod_1.z.object({
    type: zod_1.z.literal("input_audio"),
    input_audio: zod_1.z.object({
        data: exports.MediaReferenceStringSchema,
    }),
});
/**
 * Thinking content part schema.
 * Used to represent model reasoning/thinking output from various providers.
 * Normalized format across the different adapters by the providers
 */
exports.ThinkingContentPartSchema = zod_1.z.object({
    type: zod_1.z.literal("thinking"),
    content: zod_1.z.string(),
    signature: zod_1.z.string().optional(), // Anthropic's cryptographic signature
    summary: zod_1.z.string().optional(), // OpenAI provides summaries
});
/**
 * Redacted thinking content part.
 * Anthropic sometimes encrypts thinking content for safety reasons.
 */
exports.RedactedThinkingContentPartSchema = zod_1.z.object({
    type: zod_1.z.literal("redacted_thinking"),
    data: zod_1.z.string(), // Encrypted blob
});
/**
 * OpenAI text content part.
 * Used in content arrays for text portions of messages.
 * Defined as per https://platform.openai.com/docs/api-reference/chat/create#chat-create-messages
 */
const OpenAITextContentPart = zod_1.z.object({
    type: zod_1.z.union([
        zod_1.z.literal("text"),
        zod_1.z.literal("input_text"),
        zod_1.z.literal("output_text"),
    ]),
    text: zod_1.z.string(),
});
/**
 * URL for image content (http/https only).
 */
exports.OpenAIUrlImageUrl = zod_1.z.string().regex(/^https?:/);
/**
 * Base64-encoded image data URL.
 * Supported formats: png, jpeg, jpg, gif, webp
 */
const OpenAIBase64ImageUrl = zod_1.z
    .string()
    .regex(/^data:image\/(png|jpeg|jpg|gif|webp);base64,/);
/**
 * OpenAI image content part for vision-enabled models.
 * Supports URL, Langfuse media reference, or base64-encoded images.
 */
const OpenAIImageContentPart = zod_1.z.object({
    type: zod_1.z.literal("image_url"),
    image_url: zod_1.z.object({
        url: zod_1.z.union([
            exports.OpenAIUrlImageUrl,
            exports.MediaReferenceStringSchema,
            OpenAIBase64ImageUrl,
        ]),
        detail: zod_1.z.enum(["low", "high", "auto"]).optional(), // Controls how the model processes the image. Defaults to "auto". [https://platform.openai.com/docs/guides/vision/low-or-high-fidelity-image-understanding]
    }),
});
/**
 * A bare `@@@langfuseMedia:…@@@` reference used as a whole content part.
 *
 * Validates through the canonical parser but deliberately does NOT transform:
 * a transforming member of this union hands the renderer a shape its own part
 * guards reject, which is how inline media silently stopped rendering
 * (LFE-14815). The raw string must survive.
 */
exports.MediaReferencePartSchema = zod_1.z.string().refine((value) => {
    // Exactly ONE whole reference: the parser's `^…(.*)…$` is greedy, so two
    // concatenated tags would parse as one, silently mixing tag 1's type with
    // tag 2's id. Splitting first mirrors getStandaloneMediaReferenceStrings.
    const matches = value.match(exports.MEDIA_REFERENCE_PATTERN) ?? [];
    return (matches.length === 1 &&
        matches[0] === value &&
        exports.MediaReferenceStringSchema.safeParse(value).success);
});
/**
 * Array of OpenAI content parts (text, image, audio, bare media reference).
 * Used when message content is structured with multiple parts.
 */
exports.OpenAIContentParts = zod_1.z.array(zod_1.z.union([
    OpenAITextContentPart,
    OpenAIImageContentPart,
    OpenAIInputAudioContentPart,
    exports.MediaReferencePartSchema,
]));
const isMediaReferencePart = (content) => exports.MediaReferencePartSchema.safeParse(content).success;
exports.isMediaReferencePart = isMediaReferencePart;
/**
 * OpenAI output audio schema.
 * Represents audio generated by the model.
 */
const OpenAIOutputAudioSchema = zod_1.z.object({
    data: exports.MediaReferenceStringSchema,
    transcript: zod_1.z.string().optional(),
});
/**
 * OpenAI content schema for message content field.
 * Can be either a simple string or an array of structured content parts.
 * Nullable to support messages without content (e.g., tool call only).
 */
exports.OpenAIContentSchema = zod_1.z
    .union([zod_1.z.string(), exports.OpenAIContentParts])
    .nullable();
const isOpenAITextContentPart = (content) => {
    return OpenAITextContentPart.safeParse(content).success;
};
exports.isOpenAITextContentPart = isOpenAITextContentPart;
const isOpenAIImageContentPart = (content) => {
    return OpenAIImageContentPart.safeParse(content).success;
};
exports.isOpenAIImageContentPart = isOpenAIImageContentPart;
/**
 * Base ChatML message schema with full OpenAI API support.
 *
 * This schema validates messages from OpenAI's Chat Completions API and similar formats.
 * It includes support for:
 * - Text, image, and audio content (multimodal)
 * - Tool/function calling (definitions and invocations)
 * - Additional framework-specific metadata (via passthrough)
 *
 * Used primarily for frontend rendering where full message structure is needed.
 * For simple content extraction, use SimpleChatMessageSchema instead.
 *
 */
exports.BaseChatMlMessageSchema = zod_1.z
    .object({
    role: zod_1.z.string().optional(),
    name: zod_1.z.string().optional(),
    content: zod_1.z
        .union([
        zod_1.z.record(zod_1.z.string(), zod_1.z.any()),
        zod_1.z.string(),
        zod_1.z.array(zod_1.z.any()),
        exports.OpenAIContentSchema,
    ])
        .nullish(),
    audio: OpenAIOutputAudioSchema.optional(),
    additional_kwargs: zod_1.z.record(zod_1.z.string(), zod_1.z.any()).optional(),
    tools: zod_1.z.array(exports.ToolDefinitionSchema).optional(),
    tool_calls: zod_1.z.array(exports.ToolCallSchema).optional(),
    tool_call_id: zod_1.z.string().optional(),
    // Thinking/reasoning content from models
    thinking: zod_1.z.array(exports.ThinkingContentPartSchema).optional(),
    redacted_thinking: zod_1.z.array(exports.RedactedThinkingContentPartSchema).optional(),
})
    .loose();
/**
 * Minimal ChatML message schema for backend content extraction.
 *
 * This schema is intentionally permissive and lightweight, designed for:
 * - Fast validation during compact representation extraction
 * - Accepting multiple chat formats (OpenAI, LangChain, Anthropic, etc.)
 * - Avoiding expensive validation of media, audio, and tool structures
 *
 * Only validates the minimal structure needed to extract displayable content:
 * - Presence of content field (string, array, or object)
 * - Optional role field (not strictly required)
 *
 * Use this for backend operations. For frontend rendering, use BaseChatMlMessageSchema.
 */
const SimpleChatMessageSchema = zod_1.z
    .object({
    role: zod_1.z.string(),
    content: zod_1.z
        .union([
        zod_1.z.string(), // Simple string content
        zod_1.z.array(zod_1.z.any()), // Content parts array (text/image/audio)
        zod_1.z.record(zod_1.z.string(), zod_1.z.any()), // Structured content object
    ])
        .nullish(),
})
    .loose(); // Allow any extra fields
/**
 * Array of minimal ChatML messages.
 * Used for backend extraction - validates array has at least one message.
 */
exports.SimpleChatMlArraySchema = zod_1.z.array(SimpleChatMessageSchema).min(1);
/**
 * Full ChatML message schema with frontend transforms.
 *
 * NOTE: Moved to shared package to enable testing.
 * Reason: Worker tests cannot import from web package and
 * in the current setup web tests cannot import from shared.
 * Therefore, we moved frontend specific transformation logic to the
 * shared package for now, until web tests can import from shared.
 *
 * Includes transforms for frontend rendering:
 * - Spreads additional_kwargs into message (makes fields accessible)
 * - Moves unknown fields to json property (enables PrettyJsonView)
 */
exports.ChatMlMessageSchema = exports.BaseChatMlMessageSchema.refine((value) => value.content !== null || value.role !== undefined)
    .transform(({ additional_kwargs, ...other }) => ({
    ...other,
    ...additional_kwargs,
}))
    .transform(({ role, name, content, audio, type, tools, tool_calls, tool_call_id, thinking, redacted_thinking, ...other }) => ({
    role,
    name,
    content,
    audio,
    type,
    tools,
    tool_calls,
    tool_call_id,
    thinking,
    redacted_thinking,
    ...(Object.keys(other).length === 0 ? {} : { json: other }),
}));
exports.ChatMlArraySchema = zod_1.z.array(exports.ChatMlMessageSchema).min(1);
